// FleetPage (C-FLEET) — mirrors review/commerce-app/fleet.jsx.
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/pages/FleetPage.tsx, and moved off its fixtures onto
// GET /portal/api/fleet/. Every departure from the drop is marked ADAPTED and
// says why; the rest is the handoff as delivered.
//
// ADAPTED (throughout): design-system components are imported by module path
// rather than from the `@miq/design-system` barrel — AppShell.tsx explains why —
// and button handlers are `onPress`, the React Aria event the DS Button is built
// on.
//
// ADAPTED (no Region, Version or Env columns): the drop's instance table shows
// all three. `Instance` records none of them. A deployment's region is recorded
// on the provisioning request that ASKED for it, so it appears on the
// provisioning card below rather than on the instance rows; deriving it by
// matching an instance's name against a request's `instance_name` would be a
// guess dressed as a fact, and this is a page a customer reconciles against.
// What replaces those columns is what the fleet view is FOR, per its
// server-rendered twin: the mapping from an instance to the agreement it runs
// under, plus the billing identity its usage arrives stamped with.
//
// ADAPTED (the pending-subscription banner has no "received N ago"): the drop
// dates the banner. `Subscription.customer_facing_fields` does not include
// `created_at`, and the endpoint honours that list rather than reaching around
// it, so the banner says which agreement arrived and what state it is in
// without inventing a time for it.
//
// ADAPTED (no "Add instance", no "Manage in AWS Marketplace", no per-row
// actions): provisioning is a form on the server-rendered side and there is no
// endpoint behind any of these controls yet. A control that does nothing on a
// live page is the thing a customer reports as broken, so the affordance the
// page keeps is the one that works — the state of what has been asked for.
//
// ADAPTED (env filter tabs dropped): the drop filters All / Production /
// Non-production. `Instance` records no environment, so there is nothing to
// filter on.
//
// ADDED (a month selector the drop has no equivalent of): the usage column is
// cut on a window, and this page used to say "this month" and mean it. A
// running instance whose usage was last month therefore showed an em dash —
// "nothing was measured" — beside a Running badge, which reads as the metering
// being broken rather than as the reader looking at the wrong month, and there
// was no control to reach the month it metered in. It is the SAME control
// Metering carries, over the same list of months, from the same server-side
// helper: two screens showing the same instance's usage must be able to be
// pointed at the same window, or a customer cannot reconcile one against the
// other.

import { useCallback, useState } from 'react';
import { Lock, Server, ShoppingCart } from 'lucide-react';
import { Badge } from '@miq/design-system/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { ScreenFailed, ScreenLoading } from '../components/ScreenState';
import { MonthPicker } from '../components/MonthPicker';
import { RetiredBadge } from '../components/RetiredBadge';
import { ShowFormerToggle } from '../components/ShowFormerToggle';
import { ABSENT, monthOf, quantityText, relativeTime } from '../data/format';
import { agreementShown, instanceShown, isInternal, type Shown } from '../data/presentation';
import {
  readFleet,
  readFleetFor,
  type FleetInstance,
  type FleetScreen,
  type FleetSubscription,
  type Period,
} from '../data/reads';
import { usePortalRead } from '../data/usePortalRead';
import type { AuthorizedFetch } from '../auth/api';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'Fleet Management';

/**
 * How each provisioning state reads, and which badge carries it.
 *
 * Translated on this side rather than on the server: the endpoint reports the
 * core's own word, and what a state should LOOK like is the screen's business.
 * Instance and subscription states read through ../data/presentation.ts, which
 * every surface shares; the provisioning request is this page's alone.
 */
const PROVISIONING_STATE: Record<string, Shown> = {
  requested: { variant: 'outline', label: 'Requested' },
  standing_up: { variant: 'outline', label: 'Standing up' },
  completed: { variant: 'default', label: 'Completed' },
  declined: { variant: 'secondary', label: 'Declined' },
};

function shown(table: Record<string, Shown>, state: string): Shown {
  return table[state] ?? { variant: 'outline', label: state };
}

export function FleetPage() {
  useDocumentTitle(TITLE);
  // `null` means "did not ask", which the service reads as the current month.
  // Held as the absence rather than as today's date for the reason Metering
  // gives: a session left open across midnight on the 1st then follows the
  // service rather than pinning the month it was opened in.
  const [month, setMonth] = useState<string | null>(null);
  // Retired instances — an instance's only way of being former — are hidden by
  // default and come back under the toggle. Nothing to see for most customers
  // — the control is not drawn when the service hid nothing — and the history
  // for the few it concerns.
  const [showFormer, setShowFormer] = useState(false);

  // Memoised per month because `usePortalRead` holds this in a dependency
  // array; an inline arrow would re-run the request on every render and never
  // settle. Changing the month is exactly when we DO want it to re-run.
  const read = useCallback(
    (authorizedFetch: AuthorizedFetch) =>
      month === null
        ? readFleet(authorizedFetch, showFormer)
        : readFleetFor(authorizedFetch, month, showFormer),
    [month, showFormer],
  );
  const screen = usePortalRead(read);

  if (screen.status === 'loading') {
    return <ScreenLoading title={TITLE} />;
  }
  if (screen.status === 'failed') {
    return <ScreenFailed title={TITLE} message={screen.message} />;
  }
  return (
    <Fleet
      screen={screen.data}
      month={month}
      onMonth={setMonth}
      showFormer={showFormer}
      onShowFormer={setShowFormer}
    />
  );
}

function Fleet({
  screen,
  month,
  onMonth,
  showFormer,
  onShowFormer,
}: {
  screen: FleetScreen;
  month: string | null;
  onMonth: (month: string | null) => void;
  showFormer: boolean;
  onShowFormer: (showing: boolean) => void;
}) {
  const {
    instances,
    subscriptions,
    provisioning_requests: requests,
    period,
  } = screen;
  const hiddenCount = showFormer
    ? instances.filter((row) => row.retired !== null).length
    : screen.former_hidden;

  // A subscription with nothing running under it is what a newly-arrived
  // agreement looks like, and it is invisible in a list of instances — which is
  // exactly why the endpoint carries both lists.
  const running = new Set(instances.map((row) => row.subscription.public_id));
  const unprovisioned = subscriptions.filter((row) => !running.has(row.public_id));

  return (
    <div className="space-y-5 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Commerce · Fleet
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Fleet Management</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Every mDecide instance on this account and the agreement each one
            runs under.
          </p>
        </div>
        <MonthPicker
          months={screen.selectable_periods}
          selected={month}
          onSelect={onMonth}
          period={period}
        />
      </div>

      {unprovisioned.map((subscription) => (
        <UnprovisionedSubscription key={subscription.public_id} subscription={subscription} />
      ))}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Instances</CardTitle>
            <CardDescription>
              {instances.length === 1 ? '1 instance' : `${instances.length} instances`} ·
              usage totals are for {monthOf(period)}
            </CardDescription>
          </div>
          <ShowFormerToggle
            showing={showFormer}
            hiddenCount={hiddenCount}
            onChange={onShowFormer}
            of="instances"
          />
        </CardHeader>
        <CardContent className="p-0">
          {instances.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              No instances yet. One is created when a provisioning request is
              stood up under a subscription.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Instance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Agreement</TableHead>
                    <TableHead>Billing identity</TableHead>
                    <TableHead className="text-right">Usage · {monthOf(period)}</TableHead>
                    <TableHead>Last reported</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instances.map((instance) => (
                    <InstanceRow
                      key={instance.public_id}
                      instance={instance}
                      period={period}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {requests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Deployment requests</CardTitle>
            <CardDescription>
              What has been asked for, and where each ask stands. Standing an
              instance up is people's work today, so these states are what a
              person can truthfully report.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Instance name</TableHead>
                    <TableHead>Decision pack</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Requested</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => {
                    const state = shown(PROVISIONING_STATE, request.state);
                    return (
                      <TableRow key={request.public_id}>
                        <TableCell className="font-medium">{request.instance_name}</TableCell>
                        <TableCell>{request.decision_pack}</TableCell>
                        <TableCell className="font-mono text-xs">{request.region}</TableCell>
                        <TableCell>
                          <Badge variant={state.variant}>{state.label}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {relativeTime(request.requested_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2.5 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        <Lock className="size-4 shrink-0" />
        {/* The sentence about stopping an instance is gone: this page offers
            no stop control, by design, so it described an action nobody
            reading it can take. */}
        Pack pricing and agreements live with the sales channel that sold them.
      </div>
    </div>
  );
}

function InstanceRow({
  instance,
  period,
}: {
  instance: FleetInstance;
  period: Period;
}) {
  const state = instanceShown(instance);
  const agreement = agreementShown(instance.subscription);

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{instance.name}</div>
        <div className="font-mono text-xs text-muted-foreground">{instance.public_id}</div>
        {instance.access_url ? (
          // The one thing a customer actually needs from this row, and
          // until now the one thing it could not tell them: the model
          // recorded a name, a billing identity and a state, and nothing
          // that would let the person who bought it reach it.
          //
          // Absent is left silent rather than captioned "not yet
          // available": a deployment can be running before anybody has
          // recorded its address, and a caption promising one turns a
          // normal gap into a thing to chase.
          <a
            href={instance.access_url}
            className="mt-0.5 inline-block text-xs underline underline-offset-2"
            rel="noreferrer"
          >
            Open deployment
          </a>
        ) : null}
      </TableCell>
      <TableCell>
        <Badge variant={state.variant}>{state.label}</Badge>
        {/* Beside the state rather than in place of it: the state is what
            the platform last reported about the deployment, the retirement
            is what metricsIQ decided about the relationship, and the two may
            honestly disagree. */}
        {instance.retired !== null ? (
          <div className="mt-1">
            <RetiredBadge retired={instance.retired} />
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <div className="font-mono text-xs">{instance.subscription.agreement_ref}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {instance.subscription.channel} · {agreement.label}
        </div>
      </TableCell>
      <TableCell className="font-mono text-xs">{instance.client_id}</TableCell>
      <TableCell className="text-right">
        {instance.usage.length === 0 ? (
          // Not a zero. Nothing was measured, which is a different fact from a
          // measurement of nothing — and it is a fact about THIS month, so the
          // dash carries the month it is about. Unqualified beside a Running
          // badge it reads as the metering being broken.
          <span className="text-muted-foreground" title={`No usage recorded in ${monthOf(period)}`}>
            {ABSENT}
          </span>
        ) : (
          instance.usage.map((usage) => (
            <div key={usage.dimension}>
              <span className="font-medium">{quantityText(usage.quantity_recorded)}</span>{' '}
              <span className="font-mono text-[11px] text-muted-foreground">
                {usage.dimension}
              </span>
            </div>
          ))
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {relativeTime(instance.latest_usage_hour)}
      </TableCell>
    </TableRow>
  );
}

/**
 * An agreement with nothing running under it.
 *
 * The drop's "New subscription received" banner, kept for the state it was
 * designed for and honest about what it does not know: no arrival time, because
 * the model does not offer a customer one, and no "Finish setup" button,
 * because there is no endpoint behind it yet.
 */
function UnprovisionedSubscription({ subscription }: { subscription: FleetSubscription }) {
  // Not for an internal agreement: nothing is waiting to be set up under it,
  // and a card that says so would be describing a marketplace step that does
  // not exist for a deployment metricsIQ runs itself.
  if (isInternal(subscription.channel)) {
    return null;
  }
  const state = agreementShown(subscription);

  return (
    <Card className="border-link/30 bg-accent">
      <CardContent className="flex items-center gap-4 py-4">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-link">
          <ShoppingCart className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            A subscription with nothing running under it
          </div>
          <div className="text-xs text-muted-foreground">
            {subscription.agreement_ref} · {subscription.channel} · {state.label}.
            Ask us to deploy under it and the instance will appear here.
          </div>
        </div>
        <Badge variant={state.variant} className="shrink-0">
          <Server className="size-3" /> No instances
        </Badge>
      </CardContent>
    </Card>
  );
}
